import { ConstantsCodeType } from 'src/types/constantsCodes';
import { EgonDeliveryType } from 'src/types/egonRoute';
import { OptionType } from 'src/components/Select';
import onlyUnique from 'src/utils/onlyUnique';

/** Exchange service codes are never routed via EGON. */
export const EGON_EXCLUDED_SERVICE_CODES = ['DPDXS', 'GLSXS', 'GLSSKXS'];

/** EGON transfer types that are not offered in the admin (pickup / unsupported modes). */
export const EGON_EXCLUDED_TRANSFER_TYPES = [3, 10, 13, 16];

/** Service codes selectable for an EGON route: expedition, not pallet, not return, not exchange. */
export const filterEgonServiceCodes = (
    serviceCodes: ConstantsCodeType[],
    shipper: string | null | undefined
): ConstantsCodeType[] => {
    if (!shipper) {
        return [];
    }
    return serviceCodes.filter(
        (serviceCode) =>
            serviceCode.shipperCode === shipper &&
            !!serviceCode.isExpedition &&
            !serviceCode.isPallet &&
            !serviceCode.isReturn &&
            !EGON_EXCLUDED_SERVICE_CODES.includes(String(serviceCode.value))
    );
};

/** Countries available for the selected service (limited to its COUNTRY_SERVICE_CODES). */
export const getEgonCountryOptions = (
    serviceCodes: ConstantsCodeType[],
    serviceValue: string | null | undefined,
    countryOptions: OptionType[]
): OptionType[] => {
    if (!serviceValue) {
        return [];
    }
    const service = serviceCodes.find((serviceCode) => serviceCode.value === serviceValue);
    const countries = (service?.COUNTRY_SERVICE_CODES || []).filter(onlyUnique);
    return countries.map((code) => {
        const found = countryOptions.find((option) => option.value === code);
        return { value: code, label: found ? `${found.label} (${code})` : code };
    });
};

export const filterEgonDeliveries = (deliveries: EgonDeliveryType[]): EgonDeliveryType[] =>
    deliveries.filter(
        (delivery) =>
            delivery.transferType === null || !EGON_EXCLUDED_TRANSFER_TYPES.includes(Number(delivery.transferType))
    );

export const getEgonDeliveryLabel = (delivery: { id: number; name?: string | null }): string =>
    delivery.name ? `${delivery.id} – ${delivery.name}` : String(delivery.id);

export const getEgonDeliveryOptions = (deliveries: EgonDeliveryType[]): OptionType[] =>
    filterEgonDeliveries(deliveries).map((delivery) => ({
        value: delivery.id,
        label: getEgonDeliveryLabel(delivery),
    }));

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const toStringArray = (value: unknown): string[] | null =>
    Array.isArray(value) && value.every((item) => typeof item === 'string') ? (value as string[]) : null;

/**
 * Extracts conflicting countries from a 409 response of POST/PUT egon-routes.
 * Returns null when the error is not a 409; empty array when the countries are unknown.
 */
export const getEgonConflictCountries = (error: unknown): string[] | null => {
    if (!isRecord(error) || !isRecord(error.response) || error.response.status !== 409) {
        return null;
    }
    const body = error.response.data;
    if (!isRecord(body)) {
        return [];
    }
    const nested = isRecord(body.data) ? body.data : {};
    const errorData = isRecord(body.errorData) ? body.errorData : {};
    return (
        toStringArray(body.countries) ||
        toStringArray(body.conflictingCountries) ||
        toStringArray(nested.countries) ||
        toStringArray(nested.conflictingCountries) ||
        toStringArray(errorData.countries) ||
        []
    );
};

/** Validates fields that the native form validity check cannot cover (multi-select, selects). */
export const validateEgonRouteForm = (
    data: {
        ID_SERVICE_CODE?: number | null;
        COUNTRY_CODES?: string[];
        COUNTRY_CODE?: string | null;
        EGON_DELIVERY_ID?: number | null;
    },
    isNew: boolean
): { [key: string]: boolean } => {
    const errors: { [key: string]: boolean } = {};
    if (!data.ID_SERVICE_CODE) {
        errors.SERVICE_CODE_VALUE = true;
    }
    if (isNew && !(data.COUNTRY_CODES && data.COUNTRY_CODES.length)) {
        errors.COUNTRY_CODES = true;
    }
    if (!isNew && !data.COUNTRY_CODE) {
        errors.COUNTRY_CODE = true;
    }
    if (!data.EGON_DELIVERY_ID) {
        errors.EGON_DELIVERY_ID = true;
    }
    return errors;
};
